import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, Card, Divider, Theme, Typography, makeStyles } from '@material-ui/core'
import Popper from '@material-ui/core/Popper'
import { MoreVert } from '@material-ui/icons'
import clsx from 'clsx'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { buttonHoverColor, dragIconColor } from 'bpm/shared/bpmColors'

const useStyles = makeStyles((theme: Theme) => ({
    popperWrapper: {
        zIndex: 1000,
        marginRight: theme.spacing(2),
    },
    actionsWrapper: {
        backgroundColor: theme.palette.common.white,
        padding: theme.spacing(1),
        borderRadius: theme.spacing(1),
    },
    copyMoveButton: {
        width: '100%',
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: buttonHoverColor,
        },
    },
    activateDeactivateButton: {
        width: theme.spacing(20),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    divider: {
        border: 'none',
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: `${theme.spacing(0.5)}px 0`,
    },
    bulkActionButton: {
        '&:hover': {
            cursor: 'pointer',
        },
        '&.disabled': {
            color: `${dragIconColor} !important`,
        },
    },
    disabledMenuItem: {
        opacity: 0.45,
        cursor: 'not-allowed',
    },
    countLabel: {
        fontSize: pxToRem(11),
        color: '#6B7280',
        whiteSpace: 'nowrap',
    },
}))

type BulkActionButtonProps = {
    onBulkActionsClick?: (action: string) => void
    isBulkActionsEnabled?: boolean
    activeCount?: number
    inactiveCount?: number
    totalSelected?: number
}

type MenuItemRowProps = {
    label: React.ReactNode
    disabled: boolean
    onClick?: () => void
    className?: string
    'data-testid'?: string
}

const MenuItemRow: FC<MenuItemRowProps> = ({ label, disabled, onClick, className, 'data-testid': testId }) => {
    const classes = useStyles()
    return (
        <Typography
            component="div"
            role="menuitem"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            className={clsx(classes.copyMoveButton, className, { [classes.disabledMenuItem]: disabled })}
            onClick={!disabled ? onClick : undefined}
            onKeyDown={!disabled ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onClick?.()
                }
            } : undefined}
            data-testid={testId}
        >
            {label}
        </Typography>
    )
}

const BulkActionsButton: FC<BulkActionButtonProps> = ({
    onBulkActionsClick = () => { },
    isBulkActionsEnabled = false,
    activeCount = 0,
    inactiveCount = 0,
    totalSelected = 0,
}) => {
    const classes = useStyles()
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    const buttonRef = useRef(null)
    const popperRef = useRef(null)

    const onBulkActionsButtonClick = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsMenuOpen(!isMenuOpen)
        },
        [isMenuOpen]
    )

    const handleBulkActionClick = useCallback(
        (action: string) => () => {
            onBulkActionsClick?.(action)
            setIsMenuOpen(false)
        },
        [onBulkActionsClick]
    )

    const handleMenuKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setIsMenuOpen(false)
            ;(buttonRef.current as HTMLElement | null)?.focus()
            return
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            const items = Array.from(
                (popperRef.current as HTMLElement | null)?.querySelectorAll<HTMLElement>('[role="menuitem"][tabindex="0"]') ?? []
            )
            const idx = items.indexOf(document.activeElement as HTMLElement)
            const next = e.key === 'ArrowDown'
                ? (idx + 1) % items.length
                : (idx - 1 + items.length) % items.length
            items[next]?.focus()
        }
    }, [])

    useEffect(() => {
        if (isMenuOpen) {
            requestAnimationFrame(() => {
                const first = (popperRef.current as HTMLElement | null)?.querySelector<HTMLElement>('[role="menuitem"][tabindex="0"]')
                first?.focus()
            })
        }
    }, [isMenuOpen])

    useEffect(() => {
        const handleClickOutside = (e: any) => {
            if (
                popperRef?.current &&
                !(popperRef.current as HTMLElement).contains(
                    e.target as HTMLElement
                ) &&
                !(
                    buttonRef?.current &&
                    (buttonRef?.current as HTMLElement).contains(e.target)
                )
            ) {
                setIsMenuOpen(false)
            }
        }

        window.addEventListener('click', handleClickOutside)

        return () => {
            window.removeEventListener('click', handleClickOutside)
        }
    }, [])

    const isButtonEnabled = isBulkActionsEnabled || totalSelected > 0

    return (
        <>
            <Button
                onClick={onBulkActionsButtonClick}
                data-testid="btnBulkActions"
                ref={buttonRef}
                disabled={!isButtonEnabled}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                className={clsx(classes.bulkActionButton, {
                    disabled: !isButtonEnabled,
                })}
            >
                Actions &nbsp; <MoreVert />
            </Button>

            <Popper
                open={isMenuOpen}
                placement={'bottom-end'}
                anchorEl={buttonRef?.current}
                disablePortal
                ref={popperRef}
                className={classes.popperWrapper}
            >
                <Card role="menu" className={classes.actionsWrapper} onKeyDown={handleMenuKeyDown}>
                    <MenuItemRow
                        label="Copy"
                        disabled={!isBulkActionsEnabled}
                        onClick={handleBulkActionClick('copy')}
                    />
                    <MenuItemRow
                        label="Move"
                        disabled={!isBulkActionsEnabled}
                        onClick={handleBulkActionClick('move')}
                    />
                    <MenuItemRow
                        label="Delete"
                        disabled={!isBulkActionsEnabled}
                        onClick={handleBulkActionClick('delete')}
                    />
                    <Divider className={classes.divider} />
                    <MenuItemRow
                        label={
                            <>
                                Activate{' '}
                                <span className={classes.countLabel}>{inactiveCount} of {totalSelected}</span>
                            </>
                        }
                        disabled={inactiveCount === 0}
                        onClick={handleBulkActionClick('activate')}
                        className={classes.activateDeactivateButton}
                        data-testid="btnActivate"
                    />
                    <MenuItemRow
                        label={
                            <>
                                Deactivate{' '}
                                <span className={classes.countLabel}>{activeCount} of {totalSelected}</span>
                            </>
                        }
                        disabled={activeCount === 0}
                        onClick={handleBulkActionClick('deactivate')}
                        className={classes.activateDeactivateButton}
                        data-testid="btnDeactivate"
                    />
                </Card>
            </Popper>
        </>
    )
}

export default BulkActionsButton
